import { useMemo } from 'react';
import { useQueries } from 'react-query';
import {
  createQueriesState,
  queryOptions,
} from '../../api/helpers/createQueryData';

import {
  createScheduleTypesQueryKey,
  fetchScheduleTypes,
} from '../../api/scheduleTypes';
import {
  createIntervalTypesQueryKey,
  fetchIntervalTypes,
} from '../../api/intervalTypes';
import {
  createTimeIntervalsQueryKey,
  fetchTimeIntervals,
} from '../../api/timeIntervals';
import {
  createDeskNamesQueryKeys,
  deskNamesSelectors,
  fetchDeskNames,
} from '../../api/desk';
import {
  createDesksSettingsKey,
  fetchDesksSettings,
} from '../../api/desksSettings';

const options = [
  {
    queryKey: createScheduleTypesQueryKey(),
    queryFn: () => fetchScheduleTypes(),
    ...queryOptions,
  },
  {
    queryKey: createIntervalTypesQueryKey(),
    queryFn: () => fetchIntervalTypes(),
    ...queryOptions,
  },
  {
    queryKey: createTimeIntervalsQueryKey(),
    queryFn: () => fetchTimeIntervals(),
    ...queryOptions,
  },
];

export const useDeskSettingsDataQueries = ({ deskNamesParams }) => {
  const deskNamesOptions = useMemo(
    () => createDeskNamesOptions(deskNamesParams),
    [deskNamesParams]
  );

  const queries = useQueries([...options, deskNamesOptions]);
  return useMemo(() => createQueriesState(queries), [queries]);
};

export const useExistingDesksSettingsQueries = ({
  serviceCompanyId,
  acceptanceId,
}) => {
  const options = useMemo(() => {
    return createExistingDesksSettingsOptions({
      serviceCompanyId,
      acceptanceId,
    });
  }, [serviceCompanyId, acceptanceId]);

  const queries = useQueries(options);

  return useMemo(() => createQueriesState(queries), [queries]);
};

function createDeskNamesOptions(params) {
  return {
    queryKey: createDeskNamesQueryKeys(params),
    queryFn: () => fetchDeskNames(params),
    ...queryOptions,
    staleTime: 1000 * 60,
  };
}

function createExistingDesksSettingsOptions({
  serviceCompanyId,
  acceptanceId,
}) {
  return [
    {
      ...createDeskNamesOptions({ serviceCompanyId }),
      select: deskNamesSelectors.toMap,
    },
    {
      queryKey: createTimeIntervalsQueryKey(),
      queryFn: () => fetchTimeIntervals(),
      ...queryOptions,
    },
    {
      queryKey: createDesksSettingsKey({ acceptanceId }),
      queryFn: () => fetchDesksSettings({ acceptanceId }),
      ...queryOptions,
      staleTime: 10,
      enabled: Boolean(acceptanceId),
    },
  ];
}
